import React from 'react';
import PrivacyPolicy from '../../components/PrivacyPolicy/PrivacyPolicy';

function SpaceDSAPrivacy() {
  const sections = [
    {
      title: 'Introduction',
      content: (
        <>
          <p>
            SpaceDSA ("we," "our," or "the Extension") is a Chrome extension operated by
            Castle Point Innovations, a division of Cuitt LLC. SpaceDSA is a spaced repetition
            study tool for data structures and algorithms that enhances LeetCode by tracking
            problem progress and implementing spaced repetition learning techniques.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, store, and protect your
            information when you use the SpaceDSA Chrome extension and associated services.
            By using SpaceDSA, you agree to the practices described in this policy.
          </p>
        </>
      ),
    },
    {
      title: 'Information We Collect',
      content: (
        <>
          <p>We collect the following categories of information:</p>
          <p><strong>Account and Authentication Data</strong></p>
          <ul>
            <li>Google OAuth credentials (email address and profile information)</li>
            <li>LeetCode username and profile data</li>
          </ul>
          <p><strong>Study and Progress Data</strong></p>
          <ul>
            <li>Problem-solving history and progress</li>
            <li>Problem difficulty ratings</li>
            <li>Time spent on problems</li>
            <li>Solution attempts</li>
            <li>User study schedules</li>
          </ul>
          <p><strong>Preferences and Settings</strong></p>
          <ul>
            <li>Notification preferences</li>
            <li>Email address for notifications</li>
          </ul>
          <p><strong>Payment Data</strong></p>
          <ul>
            <li>
              Stripe payment and subscription data (payment details are processed
              directly by Stripe and are not stored on our servers)
            </li>
          </ul>
        </>
      ),
    },
    {
      title: 'Browser Permissions',
      content: (
        <>
          <p>SpaceDSA requests the following Chrome browser permissions:</p>
          <ul>
            <li>
              <strong>storage</strong> — To save your study progress, preferences, and
              extension settings locally on your device.
            </li>
            <li>
              <strong>identity</strong> and <strong>identity.email</strong> — To
              authenticate your account via Google OAuth.
            </li>
            <li>
              <strong>notifications</strong> — To send you study reminders and spaced
              repetition alerts based on your schedule.
            </li>
            <li>
              <strong>alarms</strong> — To schedule recurring study reminders and
              spaced repetition intervals.
            </li>
            <li>
              <strong>Content scripts on LeetCode.com</strong> — To read your
              problem-solving activity on LeetCode, track progress, and integrate
              spaced repetition features directly into the LeetCode interface.
            </li>
          </ul>
          <p>
            These permissions are used solely to provide the core functionality of the
            Extension. We do not use these permissions to collect data beyond what is
            described in this policy.
          </p>
        </>
      ),
    },
    {
      title: 'How We Use Your Information',
      content: (
        <>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Authenticate your identity and manage your account</li>
            <li>Track your LeetCode problem-solving progress</li>
            <li>Implement spaced repetition scheduling for optimal review timing</li>
            <li>Send study reminders and notifications</li>
            <li>Process payments and manage subscriptions</li>
            <li>Send transactional emails related to your account and study schedule</li>
            <li>Improve and maintain the Extension's functionality</li>
          </ul>
          <p>
            We do not sell, rent, or share your personal information with third parties
            for marketing purposes.
          </p>
        </>
      ),
    },
    {
      title: 'Third-Party Services',
      content: (
        <>
          <p>SpaceDSA integrates with the following third-party services:</p>
          <ul>
            <li>
              <strong>Firebase (Authentication and Firestore)</strong> — For user
              authentication and cloud-based data storage. Subject to the{' '}
              <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">
                Firebase Privacy Policy
              </a>.
            </li>
            <li>
              <strong>Google OAuth</strong> — For secure sign-in. Subject to{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                Google's Privacy Policy
              </a>.
            </li>
            <li>
              <strong>LeetCode</strong> — We interact with LeetCode.com via content
              scripts to read your problem-solving activity. We do not share your data
              with LeetCode.
            </li>
            <li>
              <strong>Stripe</strong> — For payment processing and subscription
              management. Payment information is handled directly by Stripe and is
              subject to the{' '}
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
                Stripe Privacy Policy
              </a>.
            </li>
            <li>
              <strong>Google Cloud App Engine</strong> — For hosting backend services
              that support the Extension.
            </li>
            <li>
              <strong>Nodemailer</strong> — For sending transactional emails such as
              study reminders and account notifications.
            </li>
          </ul>
          <p>
            Each third-party service operates under its own privacy policy. We
            encourage you to review their policies for details on how they handle
            your data.
          </p>
        </>
      ),
    },
    {
      title: 'Data Storage and Security',
      content: (
        <>
          <p>
            Your data is stored using Firebase Firestore and secured with
            industry-standard encryption in transit and at rest. Local data is stored
            in Chrome's extension storage API.
          </p>
          <p>
            We implement reasonable administrative, technical, and physical safeguards
            to protect your information. However, no method of electronic storage or
            transmission is completely secure, and we cannot guarantee absolute
            security.
          </p>
        </>
      ),
    },
    {
      title: 'Data Retention',
      content: (
        <>
          <p>
            We retain your data for as long as your account is active or as needed to
            provide you with the Extension's services. If you delete your account, we
            will delete your personal data from our systems within a reasonable
            timeframe, except where retention is required by law or for legitimate
            business purposes such as resolving disputes.
          </p>
          <p>
            Local data stored by the Extension can be removed at any time by
            uninstalling the Extension from your browser.
          </p>
        </>
      ),
    },
    {
      title: 'Your Rights',
      content: (
        <>
          <p>Depending on your jurisdiction, you may have the right to:</p>
          <ul>
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Withdraw consent for data processing</li>
            <li>Export your data in a portable format</li>
            <li>Opt out of non-essential communications</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at the email address
            provided below. We will respond to your request within a reasonable
            timeframe.
          </p>
        </>
      ),
    },
    {
      title: "Children's Privacy",
      content: (
        <p>
          SpaceDSA is not intended for use by individuals under the age of 13. We do
          not knowingly collect personal information from children under 13. If we
          become aware that we have inadvertently collected such information, we will
          take steps to delete it promptly. If you believe a child under 13 has
          provided us with personal data, please contact us immediately.
        </p>
      ),
    },
    {
      title: 'Changes to This Policy',
      content: (
        <p>
          We may update this Privacy Policy from time to time to reflect changes in
          our practices or applicable laws. When we make material changes, we will
          update the "Last Updated" date at the top of this page and, where
          appropriate, notify you via the Extension or email. Your continued use of
          SpaceDSA after any changes constitutes your acceptance of the updated policy.
        </p>
      ),
    },
    {
      title: 'Contact Us',
      content: (
        <>
          <p>
            If you have any questions, concerns, or requests regarding this Privacy
            Policy or our data practices, please contact us at:
          </p>
          <p>
            <strong>Email:</strong>{' '}
            <a href="mailto:contact@castlepointinnovations.com">
              contact@castlepointinnovations.com
            </a>
          </p>
          <p>
            <strong>Company:</strong> Castle Point Innovations — Cuitt LLC
          </p>
        </>
      ),
    },
  ];

  return <PrivacyPolicy productName="SpaceDSA" lastUpdated="March 27, 2026" sections={sections} />;
}

export default SpaceDSAPrivacy;
